<template>
  <div>
    <div class="flex-col gap-y-md">
      <general-selection-component></general-selection-component>
    </div>
  </div>
</template>

<script>
import store from "~/pages/automation/store";
import GeneralSelectionComponent from "~/pages/automation/components/GeneralSelectionComponent.vue";
import groupActionStore from "~/modules/group_action/store";
import { mapActions, mapGetters } from "vuex";
import { AutomationTypeEnum, MainSearchableClassesEnum } from "~/pages/automation/enums";
import { mapFields } from "vuex-map-fields";

export default {
  name: "DataEnrichmentSelectionStep.vue",
  components: {
      GeneralSelectionComponent
    },
  data() {
    return {
      loaded: false,
      loading: false,
      groupAction: null,
      sendEmailAtOfficeHours: false,
      visibleInCustomerPortal: false,
      customerPortalDescription: null,
    };
  },
  mounted() {
    const STORE_KEY = "$_automation";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }

    const GROUP_ACTION_STORE_KEY = "$_group_action";
    // eslint-disable-next-line no-underscore-dangle
    if (!(GROUP_ACTION_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(GROUP_ACTION_STORE_KEY, groupActionStore);
    }

    this.fetchAutomationActions()
        .finally(() => {
          this.loaded = true;
        });
  },
  watch: {
    selectedAutomationActionId: function () {
      this.$store.commit(
          "$_automation/SET_CREATING_AUTOMATION",
          Object.assign({}, this.$store.state.$_automation.creatingAutomation, { automation_action: { data: this.selectedCreatingAutomationAction } })
      );
    },
    selectedSearchId: function () {
      this.$store.commit(
          "$_automation/SET_CREATING_AUTOMATION",
          Object.assign({}, this.$store.state.$_automation.creatingAutomation, { search: { data: this.selectedCreatingSearch } })
      );
    }
  },
  computed: {
    ...mapGetters({
      automationActions: "$_automation/automationActions",
      searches: "$_automation/searches",
      searchesGroupedByType: "$_automation/searchesGroupedByType",
      extendingSearches: "$_automation/extendingSearches",
      dynamicExtendingSearches: "$_automation/dynamicExtendingSearches",
      selectedCreatingAutomationAction: "$_automation/selectedCreatingAutomationAction",
      selectedCreatingSearch: "$_automation/selectedCreatingSearch",
      selectedExtendingSearches: "$_automation/selectedExtendingSearches",
      selectedDynamicExtendingSearches: "$_automation/selectedDynamicExtendingSearches",
    }),
    ...mapFields(`$_automation`, [
      "creatingAutomation.automation_action_id",
      "creatingAutomation.search_id",
      "creatingAutomation.dynamic_extending_search_ids",
      "creatingAutomation.create_signal",
      "creatingAutomation.create_task",
      "creatingAutomation.send_email",
      "creatingAutomation.task_title",
      "creatingAutomation.task_description",
      "creatingAutomation.send_email_at_office_hours",
      "creatingAutomation.visible_in_customer_portal",
      "creatingAutomation.customer_portal_description",
      "creatingAutomation.email_user_id",
      "creatingAutomation.email_account_id",
      "creatingAutomation.email_template_id",
    ]),

    hasAutomationAction() {
      return this.selectedAutomationActionId !== null && this.selectedAutomationActionId !== undefined;
    },
    hasGroupAction() {
      return this.groupAction !== null && this.groupAction !== undefined;
    },
    selectedAutomationActionName() {
      if (this.selectedCreatingAutomationAction !== undefined) {
        return this.selectedCreatingAutomationAction.name;
      }

      return "onbekend";
    },
    selectedSearchDescription() {
      return this.selectedSearchName + " " + this.selectedExtendingSearchesDescription;
    },
    selectedSearchName() {
      if (this.selectedCreatingSearch !== undefined) {
        return this.selectedCreatingSearch.name;
      }

      return "onbekend";
    },
    selectedDynamicSearches() {
      return this.dynamicExtendingSearches.filter((extendingSearch) => {
        return this.creatingAutomation.dynamic_extending_search_ids?.indexOf(extendingSearch.id) !== -1;
      });
    },
    selectedExtendingSearchesDescription() {
      if (this.selectedDynamicSearches && this.selectedDynamicSearches.length > 0) {
        const description = this.selectedDynamicSearches.map((extendingSearch) => {
          return extendingSearch.name;
        })
            .join(", ");

        return "met " + description;
      }

      return "";
    },
    selectionStep() {
      return this.activeStep === "selection";
    },
    settingsStep() {
      return this.activeStep === "settings";
    },
    verifyStep() {
      return this.activeStep === "verify";
    },
    isNextEnabled() {
      return true;
      if (this.selectionStep) {
        return this.selectedCreatingAutomationAction !== undefined
            && this.selectedCreatingSearch !== undefined
            && this.selectedExtendingSearches !== undefined;
      }

      return false;
    },
    automation() {
      return {
        type: AutomationTypeEnum.DATA_ENRICHMENT,
        automation_action_id: this.selectedAutomationActionId,
        search_id: this.selectedSearchId,
        extending_search_ids: this.selectedExtendingSearchesIds,
        title: this.title,
        description: this.description,
        icon: null,
        is_recurring: this.isRecurring,
        auto_process: false,
        process_after: null,
        needs_verification: this.verify,
        run_interval: this.runInterval,
        create_task: this.createTask,
        task_title: this.taskTitle,
        task_description: this.taskDescription,
        send_email: this.sendEmail,
        send_email_at_office_hours: this.sendEmailAtOfficeHours,
        visible_in_customer_portal: this.visibleInCustomerPortal,
        customer_portal_description: this.customerPortalDescription,
        email_user_id: this.selectedEmailUserId,
        email_account_id: this.selectedEmailAccountId,
        email_template_id: this.selectedEmailTemplateId,
      };
    }
  },
  methods: {
    ...mapActions({
      fetchAutomationActions: "$_automation/fetchAutomationActions",
      fetchSearchesByModel: "$_automation/fetchSearchesByModel",
      getGroupActionByAutomationActionId: "$_group_action/getGroupActionByAutomationActionId",
      fetchSearchesByAutomationAction: "$_automation/fetchSearchesByAutomationAction",
      fetchExtendingSearchesByModel: "$_automation/fetchExtendingSearchesByModel",
      fetchDynamicExtendingSearchesByModel: "$_automation/fetchDynamicExtendingSearchesByModel",
      clearExtendingSearches: "$_automation/clearExtendingSearches",
      clearDynamicExtendingSearches: "$_automation/clearDynamicExtendingSearches",
      storeAutomation: "$_automation/storeAutomation",
    }),
    onSettingsChange(settings) {
      let creatingAutomation = Object.assign({}, this.$store.state.$_automation.creatingAutomation, { automation_action_settings: settings });

      return this.$store.commit("$_automation/SET_CREATING_AUTOMATION", creatingAutomation);
    },
    getTypeDescriptionByModel(model) {
      if (model === MainSearchableClassesEnum.CUSTOMER) {
        return "Klanten";
      }
      if (model === MainSearchableClassesEnum.POLICY) {
        return "Polissen";
      }
      if (model === MainSearchableClassesEnum.DAMAGE) {
        return "Schades";
      }
      if (model === MainSearchableClassesEnum.MORTGAGE) {
        return "Hypotheken";
      }
      if (model === MainSearchableClassesEnum.MORTGAGE_PART) {
        return "Leningdelen";
      }
      if (model === MainSearchableClassesEnum.OBLIGATION) {
        return "Kredieten";
      }
      if (model === MainSearchableClassesEnum.TASK) {
        return "Taken";
      }
      return null;
    },
    handleAutomationActionChange() {
      this.fetchSearchesByModel(this.selectedCreatingAutomationAction.model);
      this.getGroupActionByAutomationActionId(this.selectedCreatingAutomationAction.id)
          .then(groupAction => {
            this.groupAction = groupAction;
          });

      this.clearExtendingSearches();
    },
    handleSearchChange() {
      this.fetchDynamicExtendingSearchesByModel(this.selectedCreatingSearch.model);
    },
    filterOption(input, option) {
      return (
          option.componentOptions.children[0].text.toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
      );
    }
  },
};
</script>

<style scoped>

</style>
